import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog/Dialog';
import { Label } from '@/shared/components/ui/Label/Label';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/RadioGroup/RadioGroup';
import { Textarea } from '@/shared/components/ui/Textarea/Textarea';
import { offsetToLineColumn } from '@/shared/utils/text-lines/text-lines.util';

import {
  JSON_TEXTAREA_CLASSES,
  KEY_VALUE_ROW_ERROR_CLASSES,
  KEY_VALUE_ROW_WARNING_CLASSES,
  STRUCTURED_ISSUE_LIST_CLASSES,
} from '../structured.tokens';
import { type KeyValueImportMode } from '../useKeyValueImport/useKeyValueImport';

import { type KeyValueImportDialogProps } from './KeyValueImportDialog.types';

/**
 * The catalogue keys are written out, not composed.
 *
 * A `t(\`keyValue.import.mode${…}\`)` is invisible to a sweep for keys nothing
 * uses — which is how a catalogue accumulates dead copy, and the sweep P10.2a
 * ran is the only thing that finds it.
 */
const MODES: readonly { value: KeyValueImportMode; labelKey: string; hintKey: string }[] = [
  {
    value: 'merge',
    labelKey: 'keyValue.import.modeMerge',
    hintKey: 'keyValue.import.modeMergeHint',
  },
  {
    value: 'replace',
    labelKey: 'keyValue.import.modeReplace',
    hintKey: 'keyValue.import.modeReplaceHint',
  },
];

/**
 * Pasting a whole map in.
 *
 * A box the operator pastes into, not a clipboard read: `readText()` is not
 * exposed to web content in Firefox, is gated in Safari and is refused on an
 * insecure origin, so the "one click" version fails for most people.
 *
 * Everything the paste would do is shown BEFORE it happens — added, replaced,
 * removed, and by name the keys that were refused and why. Applying is one
 * emit, and the dialog closes rather than inviting a second one.
 *
 * Presentation only (U5): the whole projection lives in `useKeyValueImport`.
 */
export function KeyValueImportDialog({ importer, onApplied }: KeyValueImportDialogProps) {
  const { t } = useTranslation('uiKit');
  const textareaId = useId();
  const modeLabelId = useId();
  const { plan } = importer;

  return (
    <Dialog
      open={importer.isOpen}
      onOpenChange={(isOpen) => {
        if (!isOpen) importer.close();
      }}
    >
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{t('keyValue.import.title')}</DialogTitle>
          <DialogDescription>{t('keyValue.import.description')}</DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-lg">
          <div className="flex flex-col gap-sm">
            <Label htmlFor={textareaId}>{t('keyValue.import.textareaLabel')}</Label>
            <Textarea
              id={textareaId}
              value={importer.text}
              rows={8}
              spellCheck={false}
              autoComplete="off"
              // JSON is code: Latin and left to right whatever the interface
              // language is.
              dir="ltr"
              isInvalid={plan.status === 'invalid' || plan.status === 'notAnObject'}
              className={JSON_TEXTAREA_CLASSES}
              onChange={(event) => {
                importer.setText(event.target.value);
              }}
            />
          </div>

          <div className="flex flex-col gap-sm">
            <p id={modeLabelId} className="text-label font-medium text-foreground">
              {t('keyValue.import.mode')}
            </p>
            <RadioGroup
              aria-labelledby={modeLabelId}
              value={importer.mode}
              onValueChange={(mode) => {
                importer.setMode(mode as KeyValueImportMode);
              }}
            >
              {MODES.map((mode) => (
                <Label key={mode.value} className="flex items-start gap-sm font-normal">
                  <RadioGroupItem value={mode.value} className="mt-2xs shrink-0" />
                  <span className="flex flex-col items-start gap-2xs">
                    <span>{t(mode.labelKey)}</span>
                    <span className="text-caption text-muted-foreground">{t(mode.hintKey)}</span>
                  </span>
                </Label>
              ))}
            </RadioGroup>
          </div>

          <div className={STRUCTURED_ISSUE_LIST_CLASSES}>
            <p className="text-label font-medium text-foreground">{t('keyValue.import.preview')}</p>

            {plan.status === 'empty' ? (
              <p className="text-caption text-muted-foreground">
                {t('keyValue.import.previewNothing')}
              </p>
            ) : null}

            {plan.status === 'invalid' ? (
              <p role="alert" className={KEY_VALUE_ROW_ERROR_CLASSES}>
                {plan.offset === null
                  ? t('json.invalid')
                  : t('json.invalidAt', {
                      reason: t(`json.reason.${plan.code}`),
                      ...offsetToLineColumn(importer.text, plan.offset),
                    })}
              </p>
            ) : null}

            {plan.status === 'notAnObject' ? (
              <p role="alert" className={KEY_VALUE_ROW_ERROR_CLASSES}>
                {t('keyValue.import.notAnObject')}
              </p>
            ) : null}

            {plan.status === 'ready' ? (
              <>
                {importer.canApply ? (
                  <ul className={STRUCTURED_ISSUE_LIST_CLASSES}>
                    <li className="text-caption text-muted-foreground">
                      {t('keyValue.import.added', { count: plan.added.length })}
                    </li>
                    <li className="text-caption text-muted-foreground">
                      {t('keyValue.import.replaced', { count: plan.replaced.length })}
                    </li>
                    <li className="text-caption text-muted-foreground">
                      {t('keyValue.import.removed', { count: plan.removed.length })}
                    </li>
                  </ul>
                ) : (
                  <p className="text-caption text-muted-foreground">
                    {t('keyValue.import.previewNothing')}
                  </p>
                )}

                {plan.unknownKeys.length === 0 ? null : (
                  <p className={KEY_VALUE_ROW_WARNING_CLASSES}>
                    {t('keyValue.import.unknownKeys', { keys: plan.unknownKeys.join('، ') })}
                  </p>
                )}
                {plan.wrongKind.length === 0 ? null : (
                  <p className={KEY_VALUE_ROW_WARNING_CLASSES}>
                    {t('keyValue.import.wrongKind', { keys: plan.wrongKind.join('، ') })}
                  </p>
                )}
              </>
            ) : null}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={importer.close}>
            {t('action.cancel', { ns: 'common' })}
          </Button>
          <Button
            type="button"
            disabled={!importer.canApply}
            onClick={() => {
              const changed = importer.apply();
              if (changed > 0) onApplied(changed);
            }}
          >
            {t('keyValue.import.apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
